import { useState } from 'react';
import { useApi } from '../hooks/useApi.js';
import { get } from '../lib/api.js';

// ─── Types ────────────────────────────────────────────────────

interface CredentialExposureReport {
  email: string;
  is_exposed: boolean;
  exposure_severity: 'critical' | 'high' | 'medium' | 'none';
  total_breaches: number;
  active_breaches: number;
  password_exposed: boolean;
  latest_breach_date?: string;
  exposed_data_types: string[];
  breaches: Array<{
    breach_name: string;
    breach_date?: string;
    severity: string;
    password_exposed: boolean;
    data_types: string[];
    status: string;
    detected_at: string;
  }>;
  last_checked_at?: string;
  recommendation: string;
}

interface ExposureQueryLog {
  id: string;
  queried_email: string;
  queried_by_product: string;
  is_exposed: boolean;
  severity: string;
  created_at: string;
}

interface QueryLogsResponse {
  data: ExposureQueryLog[];
}

interface SingleReportResponse {
  data: CredentialExposureReport;
}

interface BulkReportResponse {
  data: CredentialExposureReport[];
}

// ─── Helpers ─────────────────────────────────────────────────

function severityColor(severity: string): string {
  if (severity === 'critical') return 'bg-red-100 text-red-700 border-red-200';
  if (severity === 'high') return 'bg-orange-100 text-orange-700 border-orange-200';
  if (severity === 'medium') return 'bg-yellow-100 text-yellow-700 border-yellow-200';
  return 'bg-emerald-100 text-emerald-700 border-emerald-200';
}

function statusBadgeClass(status: string): string {
  if (status === 'new') return 'bg-red-100 text-red-700';
  if (status === 'acknowledged') return 'bg-yellow-100 text-yellow-700';
  if (status === 'resolved') return 'bg-emerald-100 text-emerald-700';
  return 'bg-gray-100 text-gray-600';
}

function fmtDate(iso: string) {
  return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

function fmtDateTime(iso: string) {
  return new Date(iso).toLocaleString(undefined, {
    month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
  });
}

// ─── Exposure Banner ─────────────────────────────────────────

function ExposureBanner({ report }: { report: CredentialExposureReport }) {
  const bannerMap: Record<string, string> = {
    critical: 'bg-red-50 border-red-300 text-red-800',
    high: 'bg-orange-50 border-orange-300 text-orange-800',
    medium: 'bg-yellow-50 border-yellow-300 text-yellow-800',
    none: 'bg-emerald-50 border-emerald-300 text-emerald-800',
  };
  const bannerClass = bannerMap[report.exposure_severity] ?? bannerMap['none'];

  return (
    <div className={`rounded-lg border p-4 ${bannerClass}`}>
      <div className="flex items-start gap-3">
        <div className="flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-semibold text-sm">{report.email}</span>
            <span className={`px-2 py-0.5 rounded-full text-xs font-medium border ${severityColor(report.exposure_severity)}`}>
              {report.exposure_severity === 'none' ? 'No exposure' : report.exposure_severity.toUpperCase()}
            </span>
            {report.password_exposed && (
              <span className="px-2 py-0.5 rounded-full text-xs font-bold bg-red-600 text-white">
                PASSWORD EXPOSED
              </span>
            )}
          </div>
          <p className="mt-1 text-sm">{report.recommendation}</p>
          <div className="mt-2 flex flex-wrap gap-4 text-xs">
            <span>Total breaches: <strong>{report.total_breaches}</strong></span>
            <span>Active breaches: <strong>{report.active_breaches}</strong></span>
            {report.latest_breach_date && (
              <span>Latest: <strong>{fmtDate(report.latest_breach_date)}</strong></span>
            )}
            {report.last_checked_at && (
              <span>Last checked: <strong>{fmtDate(report.last_checked_at)}</strong></span>
            )}
          </div>
          {report.exposed_data_types.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1">
              {report.exposed_data_types.map(dt => (
                <span key={dt} className="px-2 py-0.5 bg-white/60 border border-current/20 rounded text-xs">
                  {dt}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>

      {report.breaches.length > 0 && (
        <div className="mt-3">
          <p className="text-xs font-semibold uppercase tracking-wide mb-2 opacity-70">Breach details</p>
          <div className="space-y-2">
            {report.breaches.map((b, idx) => (
              <div key={idx} className="bg-white/50 rounded-md px-3 py-2 text-xs border border-white/70">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-medium">{b.breach_name}</span>
                  {b.breach_date && <span className="opacity-70">{fmtDate(b.breach_date)}</span>}
                  <span className={`px-1.5 py-0.5 rounded-full text-xs font-medium ${severityColor(b.severity)}`}>{b.severity}</span>
                  <span className={`px-1.5 py-0.5 rounded-full text-xs ${statusBadgeClass(b.status)}`}>{b.status}</span>
                  {b.password_exposed && <span className="text-red-600 font-bold">password</span>}
                </div>
                {b.data_types.length > 0 && (
                  <div className="mt-1 opacity-70">Data types: {b.data_types.join(', ')}</div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Live Query Tool ──────────────────────────────────────────

function LiveQueryTool() {
  const [singleEmail, setSingleEmail] = useState('');
  const [singleResult, setSingleResult] = useState<CredentialExposureReport | null>(null);
  const [singleLoading, setSingleLoading] = useState(false);
  const [singleError, setSingleError] = useState<string | null>(null);

  const [bulkEmails, setBulkEmails] = useState('');
  const [bulkResults, setBulkResults] = useState<CredentialExposureReport[]>([]);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [bulkError, setBulkError] = useState<string | null>(null);

  async function handleSingleCheck(e: React.FormEvent) {
    e.preventDefault();
    if (!singleEmail.trim()) return;
    setSingleLoading(true);
    setSingleError(null);
    setSingleResult(null);
    try {
      const resp = await get<SingleReportResponse>(
        `/protect/credential-exposure?email=${encodeURIComponent(singleEmail.trim())}`,
      );
      setSingleResult(resp.data);
    } catch (err) {
      setSingleError(err instanceof Error ? err.message : 'Query failed');
    } finally {
      setSingleLoading(false);
    }
  }

  async function handleBulkCheck(e: React.FormEvent) {
    e.preventDefault();
    const emails = bulkEmails
      .split(/[\n,]+/)
      .map(e => e.trim())
      .filter(Boolean)
      .slice(0, 50);
    if (emails.length === 0) return;
    setBulkLoading(true);
    setBulkError(null);
    setBulkResults([]);
    try {
      const resp = await get<BulkReportResponse>(
        `/protect/credential-exposure?action=bulk&emails=${encodeURIComponent(emails.join(','))}`,
      );
      setBulkResults(resp.data);
    } catch (err) {
      setBulkError(err instanceof Error ? err.message : 'Bulk query failed');
    } finally {
      setBulkLoading(false);
    }
  }

  return (
    <div className="space-y-6">
      {/* Single email check */}
      <div className="bg-white rounded-lg border border-gray-200 p-5">
        <h3 className="text-base font-semibold text-gray-800 mb-3">Check Single Email</h3>
        <form onSubmit={handleSingleCheck} className="flex gap-3">
          <input
            type="email"
            value={singleEmail}
            onChange={e => setSingleEmail(e.target.value)}
            placeholder="user@example.com"
            className="flex-1 border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
          />
          <button
            type="submit"
            disabled={singleLoading || !singleEmail.trim()}
            className="px-4 py-2 bg-emerald-600 text-white text-sm font-medium rounded-md hover:bg-emerald-700 disabled:opacity-50"
          >
            {singleLoading ? 'Checking...' : 'Check Exposure'}
          </button>
        </form>

        {singleError && (
          <p className="mt-3 text-sm text-red-600 bg-red-50 rounded-md px-3 py-2">{singleError}</p>
        )}

        {singleResult && (
          <div className="mt-4">
            <ExposureBanner report={singleResult} />
          </div>
        )}
      </div>

      {/* Bulk check */}
      <div className="bg-white rounded-lg border border-gray-200 p-5">
        <h3 className="text-base font-semibold text-gray-800 mb-3">Bulk Query</h3>
        <p className="text-xs text-gray-500 mb-3">Enter comma-separated or line-separated emails (max 50)</p>
        <form onSubmit={handleBulkCheck} className="space-y-3">
          <textarea
            value={bulkEmails}
            onChange={e => setBulkEmails(e.target.value)}
            placeholder="alice@example.com&#10;bob@example.com&#10;carol@example.com"
            rows={4}
            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 font-mono"
          />
          <div className="flex items-center justify-between">
            <span className="text-xs text-gray-400">
              {bulkEmails.split(/[\n,]+/).map(e => e.trim()).filter(Boolean).length} emails entered
            </span>
            <button
              type="submit"
              disabled={bulkLoading || !bulkEmails.trim()}
              className="px-4 py-2 bg-emerald-600 text-white text-sm font-medium rounded-md hover:bg-emerald-700 disabled:opacity-50"
            >
              {bulkLoading ? 'Checking all...' : 'Check All'}
            </button>
          </div>
        </form>

        {bulkError && (
          <p className="mt-3 text-sm text-red-600 bg-red-50 rounded-md px-3 py-2">{bulkError}</p>
        )}

        {bulkResults.length > 0 && (
          <div className="mt-4 overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border border-gray-200 rounded-t-md">
                <tr>
                  <th className="text-left px-4 py-2 font-medium text-gray-600">Email</th>
                  <th className="text-left px-4 py-2 font-medium text-gray-600">Status</th>
                  <th className="text-left px-4 py-2 font-medium text-gray-600">Active Breaches</th>
                  <th className="text-left px-4 py-2 font-medium text-gray-600">Password Exposed</th>
                  <th className="text-left px-4 py-2 font-medium text-gray-600">Data Types</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 border border-t-0 border-gray-200">
                {bulkResults.map(r => (
                  <tr key={r.email} className="hover:bg-gray-50">
                    <td className="px-4 py-2 font-mono text-xs text-gray-900">{r.email}</td>
                    <td className="px-4 py-2">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium border ${severityColor(r.exposure_severity)}`}>
                        {r.exposure_severity === 'none' ? 'Clean' : r.exposure_severity}
                      </span>
                    </td>
                    <td className="px-4 py-2 text-gray-700">{r.active_breaches}</td>
                    <td className="px-4 py-2">
                      {r.password_exposed
                        ? <span className="text-red-600 font-bold text-xs">Yes</span>
                        : <span className="text-gray-400 text-xs">No</span>}
                    </td>
                    <td className="px-4 py-2 text-xs text-gray-500 max-w-xs truncate">
                      {r.exposed_data_types.slice(0, 4).join(', ')}{r.exposed_data_types.length > 4 ? '…' : ''}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── API Integration Guide ────────────────────────────────────

function ApiIntegrationGuide() {
  const [expanded, setExpanded] = useState(false);

  const snippet = `// From PSA, Defend, People, or any product in The One Stack:
const response = await fetch('https://protect-api.theonestack.com/protect/credential-exposure?action=service_query', {
  method: 'POST',
  headers: {
    'Content-Type': 'application/json',
    'x-integration-key': process.env.INTERNAL_SERVICE_KEY,
    'x-source-product': 'psa', // or 'defend' | 'people' | 'portal'
  },
  body: JSON.stringify({
    email: 'user@example.com',
    org_id: 'your-org-id',
  }),
});

// Response shape:
// {
//   data: {
//     email: string,
//     is_exposed: boolean,
//     exposure_severity: 'critical' | 'high' | 'medium' | 'none',
//     active_breaches: number,
//     password_exposed: boolean,
//     recommendation: string,
//   }
// }`;

  return (
    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
      <button
        className="w-full flex items-center justify-between px-5 py-4 text-left hover:bg-gray-50 transition-colors"
        onClick={() => setExpanded(e => !e)}
      >
        <div>
          <h3 className="text-base font-semibold text-gray-800">API Integration Guide</h3>
          <p className="text-xs text-gray-500 mt-0.5">How PSA, Defend, People, and Portal call the service_query endpoint</p>
        </div>
        <span className="text-gray-400 text-sm">{expanded ? '▲' : '▼'}</span>
      </button>

      {expanded && (
        <div className="px-5 pb-5 border-t border-gray-100">
          <div className="mt-4 space-y-3">
            <div className="grid grid-cols-3 gap-4 text-xs">
              <div className="bg-gray-50 rounded-md p-3">
                <p className="font-semibold text-gray-700 mb-1">Endpoint</p>
                <p className="font-mono text-gray-600 break-all">POST /protect/credential-exposure?action=service_query</p>
              </div>
              <div className="bg-gray-50 rounded-md p-3">
                <p className="font-semibold text-gray-700 mb-1">Required Headers</p>
                <p className="font-mono text-gray-600">x-integration-key: {'{INTERNAL_SERVICE_KEY}'}</p>
                <p className="font-mono text-gray-600 mt-1">x-source-product: psa | defend | people | portal</p>
              </div>
              <div className="bg-gray-50 rounded-md p-3">
                <p className="font-semibold text-gray-700 mb-1">Consumer Products</p>
                <ul className="text-gray-600 space-y-0.5">
                  <li>PSA — contact/user breach badge</li>
                  <li>Defend EDR — elevate risk score</li>
                  <li>People — flag HR records</li>
                  <li>Portal — self-service status</li>
                </ul>
              </div>
            </div>

            <div>
              <p className="text-xs font-semibold text-gray-600 mb-2">Code Example</p>
              <pre className="bg-gray-900 text-gray-100 rounded-lg p-4 text-xs overflow-x-auto leading-relaxed">
                {snippet}
              </pre>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Recent Queries ───────────────────────────────────────────

function RecentQueries() {
  const { data, loading, error, refresh } = useApi<QueryLogsResponse>(
    '/protect/credential-exposure?action=audit_log',
  );

  const logs = data?.data ?? [];

  return (
    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
      <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
        <div>
          <h3 className="text-base font-semibold text-gray-800">Recent Service Queries</h3>
          <p className="text-xs text-gray-500 mt-0.5">Last 50 service_query calls from integrated products</p>
        </div>
        <button
          onClick={refresh}
          className="text-xs text-emerald-600 hover:text-emerald-800 font-medium"
        >
          Refresh
        </button>
      </div>

      {loading ? (
        <div className="px-5 py-8 text-center text-sm text-gray-500">Loading audit log...</div>
      ) : error ? (
        <div className="px-5 py-4 text-sm text-gray-500 bg-gray-50">
          Audit log unavailable — queries are logged once the service_query endpoint is used.
        </div>
      ) : logs.length === 0 ? (
        <div className="px-5 py-8 text-center text-sm text-gray-500">
          No service queries logged yet. Integrated products will appear here when they call the service_query endpoint.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="text-left px-4 py-3 font-medium text-gray-600">Queried Email</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600">Requested By</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600">Exposure Status</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600">Severity</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600">Time</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {logs.map(log => (
                <tr key={log.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 font-mono text-xs text-gray-900">{log.queried_email}</td>
                  <td className="px-4 py-3">
                    <span className="px-2 py-0.5 bg-gray-100 text-gray-700 rounded text-xs font-medium capitalize">
                      {log.queried_by_product}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    {log.is_exposed
                      ? <span className="text-red-600 font-medium text-xs">Exposed</span>
                      : <span className="text-emerald-600 font-medium text-xs">Clean</span>}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium border ${severityColor(log.severity)}`}>
                      {log.severity}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-xs text-gray-500">{fmtDateTime(log.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────

export function CredentialExposurePage() {
  return (
    <div className="max-w-5xl mx-auto space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Credential Exposure API</h1>
        <p className="text-sm text-gray-500 mt-1">
          Query exposure status for any monitored email. Consumed by PSA, Defend, People, and Portal.
        </p>
      </div>

      {/* Live Query Tool */}
      <LiveQueryTool />

      {/* API Integration Guide */}
      <ApiIntegrationGuide />

      {/* Recent Queries */}
      <RecentQueries />
    </div>
  );
}
